import { useListPick } from '@components/ListPicker/useListPick';
import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import Select from '@components/v2/form/Select/Select';
import Switch from '@components/v2/form/Switch/Switch';
import Modal from '@components/v2/ui/Modal/Modal';
import { ModalFooterConfig } from '@components/v2/ui/Modal/modal.types';
import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { Local } from '../../../../../../../../services/modules/locals/locals.types';
import useGeneralSettingsStore from '../../../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { FreightTypeEnum } from '../../../../../../../../types/Enums';
import { formatCNPJ } from '../../../../../../../../utils/formatters';
import { Collection } from '../../../../../../../collections/collections.types';
import { useApplicationContext } from '../../../../../../../context/ApplicationContext/ApplicationContext';
import { BrandFreightType } from '../../../../../../../freight-type/brand-freight-type.types';
import { ShippingCompany } from '../../../../../../../shipping-company/shipping-company.types';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import useOrderDefinitions from '../../../../../../conditions/hooks/useOrderDefinitions';
import { SaleType } from '../../../../../../conditions/interfaces/orderCondition.interface';
import { OrderCondition } from '../../../../../../conditions/interfaces/orderCondition.interface';
import { getOrderConditionFieldsWithError } from '../../../../../../conditions/orderConditionFieldErrors.utils';
import { useOrderEditSectionsContext } from '../../../../../../edit/sections/OrderEditSections.context';
import {
  Order,
  OrderBatchItemsType,
  OrderClassificationType,
  OrderOperationsIntegrationType,
  OrderTransportCondition,
  orderChargeTypesOptions,
} from '../../../../../../order.types';
import OrderConditionApplyToAllModal from '../OrderConditionApplyToAllModal';
import { hasFieldError, isShippingCompanyVisible } from '../orderConditionDetails.utils';
import OrderConditionCustomFieldControl from './OrderConditionCustomFieldControl';
import useOrderConditionDefinitionsForm from './hooks/useOrderConditionDefinitionsForm';

interface OrderConditionDefinitionsModalProps {
  order: Order;
  condition: OrderCondition;
  isOpen: boolean;
  isReadOnlyView: boolean;
  onClose: () => void;
}

const OrderConditionDefinitionsModal = ({
  order,
  condition,
  isOpen,
  isReadOnlyView,
  onClose,
}: OrderConditionDefinitionsModalProps) => {
  const { translate } = useTranslation();
  const { isBrand, hasTotvsIntegration } = useApplicationContext();
  const { generalSettings } = useGeneralSettingsStore();
  const orderDefinitions = useOrderDefinitions();
  const { rawConditionErrors } = useOrderEditSectionsContext();
  const fieldsWithError = getOrderConditionFieldsWithError(rawConditionErrors, condition.id);
  const {
    formMethods,
    isSaving,
    customFields,
    customFieldValues,
    setCustomFieldValue,
    setSelectedOrderOperation,
    setSelectedOrderBatchItem,
    save,
    applyToAll,
  } = useOrderConditionDefinitionsForm({ order, condition, isOpen, onClose });

  const { control, setValue } = formMethods;
  const freightTypeValue = useWatch({ control, name: 'freightType' });
  const classificationTypeCode = useWatch({ control, name: 'orderClassificationTypeCode' });

  const showClassification = orderDefinitions.orderClassificationsIntegration && isBrand;

  const canFetchClassifications = showClassification && hasTotvsIntegration;

  const classificationTypesResource = useListPick<OrderClassificationType>({
    url: '/order-classifications',
    limit: 20,
    shouldFetch: canFetchClassifications,
  });

  useEffect(() => {
    if (isOpen && canFetchClassifications) {
      classificationTypesResource.onSearch('');
    }
  }, [isOpen, canFetchClassifications]);

  const selectedClassificationType = classificationTypesResource.leftItems.find(
    (type) => type.code === classificationTypeCode,
  );

  const isShippingCompanyFieldVisible = isShippingCompanyVisible({
    freightType: freightTypeValue,
    shippingCompanyIntegration: orderDefinitions.shippingCompanyIntegration,
    orderTransportCondition: orderDefinitions.orderTransportCondition,
  });

  const content = (
    <Stack sx={{ gap: 2, pt: 1 }}>
      {isBrand && (
        <Controller
          name="orderCollectionId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<Collection>
              source={{ url: 'collections', queryField: 'query' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_ORDER_COLLECTION)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              initialData={condition.collection ? [condition.collection] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'orderCollectionId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {isBrand && generalSettings.enableSaleType && (
        <Controller
          name="saleTypeId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<SaleType>
              source={{ url: '/sale-types', queryField: 'query' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_SALE_TYPE)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              initialData={condition.saleType ? [condition.saleType] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'saleTypeId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.showLocals && (
        <Controller
          name="branchId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<Local>
              source={{ url: '/locals', queryField: 'nameOrCode', order: { code: 'ASC' } }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_ORDER_BRANCH)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              initialData={condition.local ? [condition.local] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'branchId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.shippingCompanyIntegration && (
        <Controller
          name="freightType"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_FREIGHT_TYPE)}
              value={field.value ?? ''}
              onChange={(event) => field.onChange(event.target.value)}
              error={hasFieldError(fieldState, fieldsWithError, 'freightType')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
              options={FreightTypeEnum.values.map((option) => ({
                value: option.value,
                label: translate(option.label as LangEnum),
              }))}
            />
          )}
        />
      )}

      {isShippingCompanyFieldVisible && (
        <Controller
          name="shippingCompanyId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<ShippingCompany>
              source={{ url: '/shipping-companies' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_SHIPPING_COMPANY)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) =>
                `${option.code} - ${option.commercialName || option.companyName} ${formatCNPJ(option.cnpj)}`
              }
              initialData={condition.shippingCompany ? [condition.shippingCompany] : []}
              error={hasFieldError(fieldState, fieldsWithError, 'shippingCompanyId')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.orderTransportCondition !== OrderTransportCondition.NONE && (
        <Controller
          name="brandFreightTypeId"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<BrandFreightType>
              source={{ url: '/freight-type' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.GLOBAL_FREIGHT_TYPE)}
              value={field.value ?? null}
              onChange={field.onChange}
              getOptionLabel={(option) => option.description}
              initialData={condition.brandFreightType ? [condition.brandFreightType] : []}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      )}

      {showClassification && (
        <>
          <Controller
            name="orderClassificationTypeCode"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                size="small"
                disabled={isReadOnlyView}
                label={translate(LangEnum.GLOBAL_CLASSIFICATION_TYPE)}
                value={field.value ?? ''}
                onChange={(event) => {
                  field.onChange(event.target.value);
                  setValue('orderClassificationCode', null);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                options={classificationTypesResource.leftItems.map((type) => ({
                  value: type.code,
                  label: `${type.code} - ${type.name}`,
                }))}
              />
            )}
          />

          <Controller
            name="orderClassificationCode"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                size="small"
                disabled={isReadOnlyView || !selectedClassificationType}
                label={translate(LangEnum.GLOBAL_CLASSIFICATION)}
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                options={(selectedClassificationType?.orderClassifications ?? []).map(
                  (classification) => ({
                    value: classification.code,
                    label: `${classification.code} - ${classification.name}`,
                  }),
                )}
              />
            )}
          />
        </>
      )}

      {orderDefinitions.hasOrderOperationsIntegration && isBrand && (
        <Controller
          name="orderOperationCode"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<OrderOperationsIntegrationType>
              source={{ url: '/order-operations' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.ORDER_CONDITIONS_ORDER_OPERATION_TYPE_LABEL)}
              value={field.value ?? null}
              onChange={(value, options) => {
                field.onChange(value);
                setSelectedOrderOperation(options[0] ?? null);
              }}
              getOptionId={(option) => String(option.code)}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              error={hasFieldError(fieldState, fieldsWithError, 'orderOperationCode')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.hasOrderChargeTypeIntegration && isBrand && (
        <Controller
          name="orderChargeTypeCode"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.ORDER_CONDITIONS_ORDER_CHARGE_TYPE_AUTO_COMPLETE_LABEL)}
              value={field.value ?? ''}
              onChange={(event) => field.onChange(event.target.value)}
              error={hasFieldError(fieldState, fieldsWithError, 'orderChargeTypeCode')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
              options={orderChargeTypesOptions.map((option) => ({
                value: String(option.value),
                label: `${translate(option.label)} (${option.value})`,
              }))}
            />
          )}
        />
      )}

      {orderDefinitions.batchItemsIntegration && isBrand && (
        <Controller
          name="orderBatchItemCode"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncAutocomplete<OrderBatchItemsType>
              source={{ url: '/order-batch-items' }}
              size="small"
              disabled={isReadOnlyView}
              label={translate(LangEnum.ORDER_CONDITIONS_ORDER_BATCH_ITEMS_LABEL)}
              value={field.value ?? null}
              onChange={(value, options) => {
                field.onChange(value);
                setSelectedOrderBatchItem(options[0] ?? null);
              }}
              getOptionId={(option) => option.code}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              error={hasFieldError(fieldState, fieldsWithError, 'orderBatchItemCode')}
              helperText={fieldState.error?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {orderDefinitions.showEmitInvoice && (
        <Controller
          name="emitInvoice"
          control={control}
          render={({ field }) => (
            <Switch
              label={translate(LangEnum.ORDER_CONDITIONS_EMIT_INVOICE)}
              checked={!!field.value}
              disabled={isReadOnlyView}
              onChange={(_, checked) => field.onChange(checked)}
            />
          )}
        />
      )}

      {customFields.map((config) => (
        <OrderConditionCustomFieldControl
          key={config.id}
          config={config}
          value={customFieldValues[config.id]}
          isDisabled={isReadOnlyView}
          onChange={(value) => setCustomFieldValue(config.id, value)}
        />
      ))}
    </Stack>
  );

  const footer: ModalFooterConfig = isReadOnlyView
    ? { primaryAction: { label: LangEnum.GLOBAL_CLOSE, onClick: onClose } }
    : {
        primaryAction: {
          label: LangEnum.GLOBAL_SAVE,
          isLoading: isSaving,
          onClick: save,
        },
        secondaryAction: {
          label: LangEnum.GLOBAL_CANCEL_LABEL,
          onClick: onClose,
        },
      };

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="expanded"
        onClose={onClose}
        footer={footer}
        header={{ title: LangEnum.ORDER_CONDITION_ADITIONAL_CONDITION_TITLE }}
      >
        {content}
      </Modal>

      <OrderConditionApplyToAllModal {...applyToAll} />
    </>
  );
};

export default OrderConditionDefinitionsModal;
